import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Card } from '../../components/ui/Card';
import { LoadingPanel, SkeletonRow } from '../../components/ui/Loader';
import { api } from '../../lib/api';
import { useCan } from '../../stores/session';
import { BarSeries } from './BarSeries';
import type { ArtistStats, HostStats, TrackDetail, TrackStats } from './types';

/**
 * Your own numbers, off the dashboard (§5).
 *
 * ## Why this is a page and not a rail
 *
 * The dashboard's job is "what should I listen to". Reach figures sitting in a
 * column beside it were a second job on the same screen, competing for the same
 * attention and getting a third of the width to do it in - which is why they
 * were three numbers rather than anything you could act on.
 *
 * Given a page, the same data can answer the questions an artist actually has:
 * is this going up or down, where are people finding it, and which track is
 * doing the work.
 *
 * ## Two tabs because there are two jobs
 *
 * Making music and running a room are different things with different measures,
 * and most people do only one of them. The host tab is absent entirely for
 * somebody without the role rather than empty - an empty tab is an invitation
 * to wonder what you are missing.
 */
export function StatsPage() {
  const [params, setParams] = useSearchParams();
  const canHost = useCan('host.broadcast');

  /*
   * The tab lives in the URL, so "look at my host numbers" survives a reload
   * and the back button undoes a tab change rather than leaving the page.
   */
  const requested = params.get('tab');
  const tab = requested === 'host' && canHost ? 'host' : 'artist';

  const setTab = (next: string) => {
    const updated = new URLSearchParams(params);
    updated.set('tab', next);
    setParams(updated, { replace: true });
  };

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-10">
      <h1 className="text-3xl font-bold tracking-tight text-ink">Your stats</h1>

      {canHost && (
        <div role="tablist" className="mt-6 flex gap-1 border-b border-line pb-3">
          {[
            { id: 'artist', label: 'As an artist' },
            { id: 'host', label: 'As a host' },
          ].map((option) => (
            <button
              key={option.id}
              role="tab"
              type="button"
              aria-selected={tab === option.id}
              data-stats-tab={option.id}
              onClick={() => setTab(option.id)}
              className={`rounded-[var(--radius-pill)] px-4 py-2 text-sm transition-colors ${
                tab === option.id ? 'bg-raised font-medium text-ink' : 'text-soft hover:text-ink'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {tab === 'host' ? <HostTab /> : <ArtistTab />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Artist                                                                      */
/* -------------------------------------------------------------------------- */

function ArtistTab() {
  const { data, isPending } = useQuery({
    queryKey: ['stats', 'artist'],
    queryFn: () => api.get<ArtistStats>('/me/stats'),
  });

  if (isPending) return <LoadingPanel label="Counting" className="mt-8" />;
  if (!data) return null;

  const { overview } = data;

  if (overview.tracks === 0) {
    return (
      <Card className="mt-8 flex flex-col items-start gap-3 p-8">
        <p className="text-[15px] text-ink">Nothing to count yet.</p>
        <p className="max-w-md text-sm leading-relaxed text-soft">
          Numbers here start the first time somebody presses play on something of yours.
        </p>
        <Link to="/upload" className="text-sm text-accent hover:underline">
          Drop a track →
        </Link>
      </Card>
    );
  }

  return (
    <div className="mt-8 flex flex-col gap-8">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Figure value={overview.plays} label="plays" trend={trend(overview.plays_7d, overview.plays_prev_7d)} />
        <Figure value={overview.listeners} label="listeners" />
        <Figure value={overview.saves} label="saves" />
        <Figure value={overview.reposts} label="reposts" />
        <Figure value={overview.followers} label="followers" />
        {/*
          The one figure that says whether people listened rather than clicked.
          A catalogue at nine percent is being bounced off, and no play count
          will ever say so.
        */}
        <Figure
          value={overview.completion === null ? '—' : `${Math.round(overview.completion * 100)}%`}
          label="listened through"
          tone="text-fresh"
        />
      </div>

      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-lg font-bold tracking-tight text-ink">Plays</h2>
          <span className="meta text-faint">last 28 days</span>
        </div>
        <Card className="p-5">
          <BarSeries points={data.daily} />
        </Card>
      </section>

      {data.sources.length > 0 && (
        <section className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="text-lg font-bold tracking-tight text-ink">Where they came from</h2>
            <span className="meta text-faint">last 90 days</span>
          </div>
          <Card className="flex flex-col gap-2.5 p-5">
            {data.sources.map((row) => (
              <SourceRow
                key={row.source}
                source={row.source}
                plays={row.plays}
                of={data.sources.reduce((sum, s) => sum + s.plays, 0)}
              />
            ))}
          </Card>
        </section>
      )}

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-bold tracking-tight text-ink">Track by track</h2>
        <ul className="flex flex-col gap-2">
          {data.tracks.map((track) => (
            <li key={track.id}>
              <TrackRow track={track} />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/**
 * One track, and its detail on demand.
 *
 * Expanded in place rather than routed to. Comparing two tracks means opening
 * one, reading it and opening the next, and a page load between each turns a
 * comparison into a memory test.
 */
function TrackRow({ track }: { track: TrackStats }) {
  const [open, setOpen] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['stats', 'track', track.id],
    queryFn: () => api.get<TrackDetail>(`/me/stats/tracks/${track.id}`),
    // Only when somebody asks. Forty tracks would otherwise be forty queries
    // for detail nobody has looked at.
    enabled: open,
  });

  return (
    <Card className={open ? 'border-line-strong' : ''}>
      <button
        type="button"
        aria-expanded={open}
        data-track-row={track.id}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center gap-3.5 p-3 text-left"
      >
        {track.artwork_small ? (
          <img src={track.artwork_small} alt="" className="h-11 w-11 shrink-0 rounded-[var(--radius-control)] object-cover" />
        ) : (
          <div
            aria-hidden
            className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
            style={{ backgroundImage: 'repeating-linear-gradient(45deg,var(--color-blank) 0 6px,var(--color-blank-alt) 6px 12px)' }}
          />
        )}

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium text-ink">{track.title}</span>
          <span className="truncate font-mono text-[10px] text-faint">
            {track.status !== 'ready' ? track.status : track.visibility}
            {track.live_plays > 0 && ` · played on air ${track.live_plays}×`}
          </span>
        </span>

        <span className="hidden shrink-0 gap-6 sm:flex">
          <Mini value={track.plays} label="plays" />
          <Mini value={track.listeners} label="listeners" />
          <Mini value={track.saves} label="saves" />
          <Mini
            value={track.completion === null ? '—' : `${Math.round(track.completion * 100)}%`}
            label="through"
          />
        </span>

        <span aria-hidden className={`shrink-0 text-faint transition-transform ${open ? 'rotate-90' : ''}`}>
          ›
        </span>
      </button>

      {open && (
        <div className="flex flex-col gap-5 border-t border-line p-5">
          {isPending && <SkeletonRow />}

          {data && (
            <>
              <div className="flex flex-col gap-2">
                <span className="meta-caps text-faint">Plays · last 28 days</span>
                <BarSeries points={data.daily} />
              </div>

              {data.sources.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="meta-caps text-faint">Where they came from</span>
                  {data.sources.map((row) => (
                    <SourceRow
                      key={row.source}
                      source={row.source}
                      plays={row.plays}
                      of={data.sources.reduce((sum, s) => sum + s.plays, 0)}
                    />
                  ))}
                </div>
              )}

              {/* The follow-up an artist actually wants after a good week. */}
              {data.hosts.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="meta-caps text-faint">Hosts who played it</span>
                  <ul className="flex flex-col gap-1.5">
                    {data.hosts.map((host, index) => (
                      <li key={`${host.username}-${index}`} className="flex items-baseline justify-between gap-3 font-mono text-[11px]">
                        <Link to={`/${host.username}`} className="text-soft hover:text-ink hover:underline">
                          {host.display_name}
                        </Link>
                        <span className={host.skipped ? 'text-faint' : 'text-fresh'}>
                          {host.skipped ? 'cut short' : 'played through'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.sources.length === 0 && data.hosts.length === 0 && (
                <p className="text-sm text-soft">Nothing to break down yet.</p>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Host                                                                        */
/* -------------------------------------------------------------------------- */

function HostTab() {
  const { data, isPending, error } = useQuery({
    queryKey: ['stats', 'host'],
    queryFn: () => api.get<HostStats>('/me/stats/host'),
  });

  if (isPending) return <LoadingPanel label="Counting" className="mt-8" />;
  if (error) {
    return (
      <div className="mt-8">
        <Alert tone="error">Those could not be loaded.</Alert>
      </div>
    );
  }
  if (!data?.station) {
    return (
      <Card className="mt-8 flex flex-col items-start gap-3 p-8">
        <p className="text-[15px] text-ink">You have not been on air yet.</p>
        <Link to="/console" className="text-sm text-accent hover:underline">
          Open your console →
        </Link>
      </Card>
    );
  }

  const { overview } = data;

  return (
    <div className="mt-8 flex flex-col gap-8">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {/*
          First, because it is the closest thing to a measure of doing the job.
          Not how many people heard you - how many artists you were first about.
        */}
        <Figure value={overview.first_time_artists} label="played first" tone="text-fresh" />
        <Figure value={overview.artists_played} label="artists played" />
        <Figure value={overview.tracks_played} label="tracks played" />
        <Figure value={`${overview.hours_on_air}h`} label="on air" />
        <Figure value={overview.peak_listeners} label="peak listeners" />
        <Figure value={overview.submissions} label="submissions" />
      </div>

      <section className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-lg font-bold tracking-tight text-ink">Tracks played</h2>
          <span className="meta text-faint">last 28 days</span>
        </div>
        <Card className="p-5">
          <BarSeries points={data.daily} />
        </Card>
      </section>

      <div className="grid gap-8 lg:grid-cols-2">
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-bold tracking-tight text-ink">Artists you play most</h2>
          <Card className="flex flex-col gap-2.5 p-5">
            {data.top_artists.length === 0 ? (
              <p className="text-sm text-soft">Nothing played yet.</p>
            ) : (
              data.top_artists.map((artist) => (
                <div key={artist.username} className="flex items-baseline justify-between gap-3">
                  <Link to={`/${artist.username}`} className="truncate text-sm text-ink hover:underline">
                    {artist.display_name}
                  </Link>
                  <span className="shrink-0 font-mono text-[11px] text-faint">{artist.plays}</span>
                </div>
              ))
            )}
          </Card>
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-bold tracking-tight text-ink">How the room runs</h2>
          <Card className="flex flex-col gap-2.5 p-5">
            <Row label="submissions waiting" value={overview.waiting} />
            {/* The most useful number a host has about their own taste. */}
            <Row label="cut short" value={overview.skipped} tone={overview.skipped > 0 ? 'text-hot-text' : undefined} />
            <Row label="reviews written" value={overview.reviews} />
            <Row label="chat messages" value={overview.chat_messages} />
          </Card>
        </section>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Bits                                                                        */
/* -------------------------------------------------------------------------- */

function Figure({
  value,
  label,
  tone = 'text-ink',
  trend,
}: {
  value: number | string;
  label: string;
  tone?: string;
  trend?: { direction: 'up' | 'down' | 'flat'; percent: number } | null;
}) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className={`text-2xl font-bold tracking-tight ${tone}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
      <span className="meta text-faint">{label}</span>
      {trend && trend.direction !== 'flat' && (
        <span
          className={`font-mono text-[10px] ${trend.direction === 'up' ? 'text-fresh' : 'text-hot-text'}`}
        >
          {trend.direction === 'up' ? '▲' : '▼'} {trend.percent}% vs last week
        </span>
      )}
    </Card>
  );
}

function Mini({ value, label }: { value: number | string; label: string }) {
  return (
    <span className="flex w-16 flex-col text-right">
      <span className="text-sm font-medium text-ink">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
      <span className="font-mono text-[9px] text-faint">{label}</span>
    </span>
  );
}

function Row({ label, value, tone = 'text-ink' }: { label: string; value: number; tone?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-sm text-soft">{label}</span>
      <span className={`font-mono text-[12px] ${tone}`}>{value.toLocaleString()}</span>
    </div>
  );
}

function SourceRow({ source, plays, of }: { source: string; plays: number; of: number }) {
  const share = of > 0 ? Math.round((plays / of) * 100) : 0;

  return (
    <div className="flex items-center gap-3">
      <span className="w-24 shrink-0 font-mono text-[11px] text-soft">{SOURCE_LABEL[source] ?? source}</span>
      <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-[var(--radius-pill)] bg-track">
        <span className="block h-full rounded-[var(--radius-pill)] bg-accent" style={{ width: `${share}%` }} />
      </span>
      <span className="w-20 shrink-0 text-right font-mono text-[10px] text-faint">
        {plays.toLocaleString()} · {share}%
      </span>
    </div>
  );
}

/** `plays.source` in words. The enum is §6.3's. */
const SOURCE_LABEL: Record<string, string> = {
  feed: 'the feed',
  profile: 'your profile',
  chart: 'the charts',
  embed: 'embeds',
  search: 'search',
  radio: 'live rooms',
  collection: 'albums',
  track: 'track pages',
  dashboard: 'the home page',
};

/** Week on week, as a direction and a percentage. */
function trend(now: number, before: number): { direction: 'up' | 'down' | 'flat'; percent: number } | null {
  // No baseline means no comparison. "+100%" against a week with nothing in it
  // is a true statement about nothing.
  if (before === 0) return null;
  if (now === before) return { direction: 'flat', percent: 0 };

  return {
    direction: now > before ? 'up' : 'down',
    percent: Math.abs(Math.round(((now - before) / before) * 100)),
  };
}
